import type { ApprovalKindLadder, ApprovalTierInput } from '@chukua/common';
import { Money } from '@chukua/common';
import { Alert, Button, Drawer, Form } from 'antd';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import type { RoleWithPermissions } from '../roles/roles.api';
import { TierRow } from './TierRow';

const CURRENCY = 'KES';

interface TierFormValue {
  amount: number;
  roleKey: string;
}

interface EditLadderDrawerProps {
  ladder: ApprovalKindLadder | null;
  roleOptions: RoleWithPermissions[];
  error: string | null;
  submitting: boolean;
  onSave: (tiers: ApprovalTierInput[]) => Promise<boolean>;
  onClose: () => void;
}

/** One kind's whole ladder, edited together — amounts in KES major units, converted at the boundary with Money.parse/toDecimalString. */
export function EditLadderDrawer({ ladder, roleOptions, error, submitting, onSave, onClose }: EditLadderDrawerProps) {
  const [form] = Form.useForm<{ tiers: TierFormValue[] }>();

  useEffect(() => {
    if (!ladder) return;
    form.setFieldsValue({
      tiers: ladder.tiers.map((tier) => ({ amount: Number(Money.fromJSON(tier.amount).toDecimalString()), roleKey: tier.roleKey })),
    });
  }, [ladder, form]);

  async function submit(values: { tiers: TierFormValue[] }) {
    const tiers: ApprovalTierInput[] = (values.tiers ?? []).map((tier) => ({
      amount: Money.parse(String(tier.amount), CURRENCY).toJSON(),
      roleKey: tier.roleKey,
    }));
    if (await onSave(tiers)) form.resetFields();
  }

  return (
    <Drawer open={ladder !== null} onClose={onClose} title={ladder ? `Edit ${ladder.name} ladder` : ''} placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.List name="tiers">
          {(fields, { add, remove, move }) => (
            <div className="space-y-3">
              {fields.map((field, index) => (
                <TierRow
                  key={field.key}
                  fieldName={field.name}
                  isFirst={index === 0}
                  isLast={index === fields.length - 1}
                  roleOptions={roleOptions}
                  onMoveUp={() => move(index, index - 1)}
                  onMoveDown={() => move(index, index + 1)}
                  onRemove={() => remove(field.name)}
                />
              ))}
              <Button type="dashed" icon={<Plus className="size-3.5" />} onClick={() => add()} block>
                Add tier
              </Button>
            </div>
          )}
        </Form.List>
        <Form.Item className="mb-0 pt-4">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Save ladder
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
